import React, { useState, useCallback, useEffect } from "react";
import { Modal, ModalBody } from "reactstrap";
import "./audit_styles.css";
import AuditChatbotDisplay from "./AuditChatbotDisplay";

const SpecialMessageModal = ({ type, message, isModalOpen, closeModal }) => {
  const specialTypes = ["TET", "MENU_CONFIGURATION", "CHATBOT"];
  const [chatbotTrees, setChatbotTrees] = useState([]);
  const toggleAmiNode = (node, nodeId) => {
    if (!node) return node;
    if (node.id === nodeId) return { ...node, expanded: !node.expanded };
    if (Array.isArray(node.children) && node.children.length > 0) {
      return {
        ...node,
        children: node.children.map((child) => toggleAmiNode(child, nodeId)),
      };
    }
    return node;
  };
  const onToggleAmiChatbot = useCallback((nodeId, blockIdx) => {
    setChatbotTrees((prevTrees) =>
      prevTrees.map((tree, idx) =>
        idx === blockIdx ? toggleAmiNode(tree, nodeId) : tree
      )
    );
  }, []);
  useEffect(() => {
    if (type === "CHATBOT" && message) {
      const { jsonBlocks } = extractJsonBlocksAndSegments(message);
      const allRoots = jsonBlocks.map((block) => {
        try {
          const parsed = JSON.parse(block);
          // Safe check for hierarchical array
          if (
            parsed &&
            parsed.hierarchical &&
            Array.isArray(parsed.hierarchical) &&
            parsed.hierarchical.length > 0
          ) {
            return parsed.hierarchical[0];
          } else {
            return null;
          }
        } catch (e) {
          return null;
        }
      });
      setChatbotTrees(allRoots);
    }
  }, [type, message]);

  const TETRuleMap = {
    recMinRange: "Min Recommended Range of Rules",
    recMaxRange: "Max Recommended Range of Rules",
    aboveAndBelowMessage: "Above and Below Message",
    showGenericMessage: "Show Generic Message?",
    genericMessage: "Generic Message",
  };
  const extractJsonBlocksAndSegments = (str) => {
    let results = [],
      segments = [],
      cursor = 0,
      lastEnd = 0;
    while (cursor < str.length) {
      if (str[cursor] === "{") {
        // Between-text segment up to this object
        if (cursor > lastEnd) {
          segments.push(str.substring(lastEnd, cursor));
        }
        let depth = 0,
          start = cursor;
        do {
          if (str[cursor] === "{") depth++;
          else if (str[cursor] === "}") depth--;
          cursor++;
        } while (depth > 0 && cursor < str.length);
        results.push(str.substring(start, cursor));
        lastEnd = cursor;
      } else {
        cursor++;
      }
    }
    // Capture any text after the last JSON block
    if (lastEnd < str.length) {
      segments.push(str.substring(lastEnd));
    }
    return { jsonBlocks: results, textSegments: segments };
  };
  const extractMenuArraysAndSegments = (str) => {
    let arrays = [],
      segments = [],
      cursor = 0,
      lastEnd = 0;
    while (cursor < str.length) {
      if (str[cursor] === "[") {
        if (cursor > lastEnd) segments.push(str.substring(lastEnd, cursor));
        let depth = 0,
          start = cursor;
        do {
          if (str[cursor] === "[") depth++;
          else if (str[cursor] === "]") depth--;
          cursor++;
        } while (depth > 0 && cursor < str.length);
        arrays.push(str.substring(start, cursor));
        lastEnd = cursor;
      } else {
        cursor++;
      }
    }
    if (lastEnd < str.length) segments.push(str.substring(lastEnd));
    return { arrays, textSegments: segments };
  };
  const mapPreKeys = (obj, keyMap) => {
    if (Array.isArray(obj)) return obj.map((item) => mapPreKeys(item, keyMap));
    else if (typeof obj === "object" && obj !== null) {
      const newObj = {};
      Object.entries(obj).forEach(([k, v]) => {
        const newKey = keyMap[k] || k;
        newObj[newKey] = mapPreKeys(v, keyMap);
      });
      return newObj;
    }
    return obj;
  };

  const renderDetails = (details) => {
    if (type === "TET" && details.startsWith("CONTENT:")) {
      const htmlTagPattern = /<[^>]+>/;
      if (htmlTagPattern.test(details)) {
        return (
          <div
            style={{
              fontSize: "1.2rem",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              textAlign: "left",
              fontWeight: "normal",
            }}
            dangerouslySetInnerHTML={{ __html: details }}
          />
        );
      }
    }
    if (type === "TET" && details.startsWith("RULE:")) {
      const { jsonBlocks, textSegments } =
        extractJsonBlocksAndSegments(details);
      let rendered = [];
      for (let i = 0; i < jsonBlocks.length; i++) {
        // Always print the headline/segment between JSONs -- ensures 'to (new):' always appears!
        rendered.push(
          <div key={"text-" + i} style={{ margin: "6px 0" }}>
            {textSegments[i]}
          </div>
        );
        try {
          let parsed = JSON.parse(jsonBlocks[i]);
          let rules = parsed.rule;
          let rootNoRules = { ...parsed };
          delete rootNoRules.rule;
          let rootFriendly = mapPreKeys(rootNoRules, TETRuleMap);
          rendered.push(
            <pre
              key={i + "-root"}
              style={{
                fontWeight: "bold",
                fontSize: "1.08rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
                textAlign: "left",
                background: "#f8f8fa",
                padding: "8px",
                borderRadius: "5px",
                margin: "6px 0",
              }}
            >
              {JSON.stringify(rootFriendly, null, 2)}
            </pre>
          );
          // Render rule array as a table if present
          if (Array.isArray(rules) && rules.length > 0) {
            // Make column headers from keys of first rule
            const ruleKeys = Object.keys(rules[0]);
            rendered.push(
              <table
                key={i + "-table"}
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  margin: "12px 0",
                  background: "#fff",
                  wordBreak: "normal",
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: "#2f9ece", color: "#fff" }}>
                    <th style={{ padding: "6px", border: "1px solid #bbb" }}>
                      Min Temp Condition
                    </th>
                    <th style={{ padding: "6px", border: "1px solid #bbb" }}>
                      Min Temp (°C)
                    </th>
                    <th style={{ padding: "6px", border: "1px solid #bbb" }}>
                      Max Temp Condition
                    </th>
                    <th style={{ padding: "6px", border: "1px solid #bbb" }}>
                      Max Temp (°C)
                    </th>
                    <th style={{ padding: "6px", border: "1px solid #bbb" }}>
                      Min Duration Condition
                    </th>
                    <th style={{ padding: "6px", border: "1px solid #bbb" }}>
                      Min Duration (Hrs)
                    </th>
                    <th style={{ padding: "6px", border: "1px solid #bbb" }}>
                      Max Duration Condition
                    </th>
                    <th style={{ padding: "6px", border: "1px solid #bbb" }}>
                      Max Duration (Hrs)
                    </th>
                    <th style={{ padding: "6px", border: "1px solid #bbb" }}>
                      Message
                    </th>
                    <th style={{ padding: "6px", border: "1px solid #bbb" }}>
                      Color
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map((row, ridx) => (
                    <tr key={ridx}>
                      {ruleKeys.map((k, kk) =>
                        k === "color" ? (
                          <td
                            key={kk}
                            style={{ border: "1px solid #bbb", padding: "6px" }}
                          >
                            <span
                              style={{
                                display: "inline-block",
                                width: "16px",
                                height: "16px",
                                borderRadius: "4px",
                                background: row[k],
                                marginRight: "6px",
                                verticalAlign: "middle",
                              }}
                            />
                          </td>
                        ) : k === "message" ? (
                          <td
                            key={kk}
                            style={{
                              border: "1px solid #bbb",
                              padding: "6px",
                              textAlign: "left",
                              fontWeight: "normal",
                            }}
                          >
                            {row[k]}
                          </td>
                        ) : (
                          <td
                            key={kk}
                            style={{ border: "1px solid #bbb", padding: "6px" }}
                          >
                            {row[k]}
                          </td>
                        )
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            );
          } else if (Array.isArray(rules) && rules.length === 0) {
            rendered.push(
              <div
                key={i + "-emptyrule"}
                style={{ margin: "7px", fontStyle: "italic" }}
              >
                (No rules present)
              </div>
            );
          }
        } catch (e) {
          rendered.push(
            <pre
              key={i + "-err"}
              style={{
                fontWeight: "bold",
                fontSize: "1.08rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
                textAlign: "left",
                background: "#f8f8fa",
                padding: "8px",
                borderRadius: "5px",
                margin: "6px 0",
              }}
            >
              {jsonBlocks[i]}
            </pre>
          );
        }
      }
      // Any trailing text after last object (always print, even if just 'to (new):')
      if (textSegments.length > jsonBlocks.length) {
        rendered.push(
          <div key={"text-last"} style={{ margin: "6px 0" }}>
            {textSegments[textSegments.length - 1]}
          </div>
        );
      }
      return (
        <div
          style={{
            fontWeight: "bold",
            fontSize: "1.2rem",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            textAlign: "center",
          }}
        >
          {rendered}
        </div>
      );
    }

    if (type === "CHATBOT") {
      const { jsonBlocks, textSegments } =
        extractJsonBlocksAndSegments(details);
      let rendered = [];
      for (let i = 0; i < jsonBlocks.length; i++) {
        // Always render between-text (label like 'to (new):')
        rendered.push(
          <div
            key={"text-" + i}
            style={{ margin: "6px 0", fontWeight: "bold" }}
          >
            {textSegments[i]}
          </div>
        );
        let chatbotJson = null;
        try {
          chatbotJson = JSON.parse(jsonBlocks[i]);
        } catch (e) {
          chatbotJson = null;
          rendered.push(<pre key={"ami-json-fail-" + i}>{jsonBlocks[i]}</pre>);
        }
        // Render hierarchical tree if present.
        if (chatbotJson && Array.isArray(chatbotJson.hierarchical)) {
          rendered.push(
            <div
              key={"ami-tree-" + i}
              style={{
                textAlign: "left",
                fontWeight: "normal",
                fontSize: "medium",
              }}
            >
              <AuditChatbotDisplay
                node={chatbotTrees[i]}
                depth={0}
                onToggle={(nodeId) => onToggleAmiChatbot(nodeId, i)}
              />
            </div>
          );
        } else if (chatbotJson) {
          // If fallback (not hierarchical, or parse error)
          rendered.push(
            <pre
              key={"ami-json-plain-" + i}
              style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
                fontSize: "1rem",
                padding: "8px",
                background: "#f8f8fa",
                borderRadius: "4px",
                marginTop: "10px",
                textAlign: "left",
              }}
            >
              {JSON.stringify(chatbotJson, null, 2)}
            </pre>
          );
        }
      }
      if (
        textSegments.length > jsonBlocks.length &&
        textSegments[textSegments.length - 1].trim()
      ) {
        rendered.push(
          <div
            key={"text-last"}
            style={{ margin: "6px 0", fontWeight: "bold" }}
          >
            {textSegments[textSegments.length - 1]}
          </div>
        );
      }
      return (
        <div
          style={{
            fontWeight: "bold",
            fontSize: "1.15rem",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            textAlign: "center",
          }}
        >
          {rendered}
        </div>
      );
    }

    if (type === "MENU_CONFIGURATION") {
      const { arrays, textSegments } = extractMenuArraysAndSegments(details);
      let rendered = [];
      for (let i = 0; i < arrays.length; i++) {
        rendered.push(
          <div key={"text-" + i} style={{ margin: "6px 0" }}>
            {textSegments[i]}
          </div>
        );
        try {
          const menuArr = JSON.parse(arrays[i]);
          rendered.push(
            <table
              key={"table-" + i}
              style={{
                width: "100%",
                borderCollapse: "collapse",
                margin: "12px 0",
                background: "#fff",
              }}
            >
              <thead>
                <tr style={{ backgroundColor: "#2f9ece", color: "#fff" }}>
                  <th style={{ padding: "6px", border: "1px solid #bbb" }}>
                    Type
                  </th>
                  <th style={{ padding: "6px", border: "1px solid #bbb" }}>
                    Page Name
                  </th>
                  <th style={{ padding: "6px", border: "1px solid #bbb" }}>
                    Display Name
                  </th>
                  <th style={{ padding: "6px", border: "1px solid #bbb" }}>
                    Url/Template Name
                  </th>
                  <th style={{ padding: "6px", border: "1px solid #bbb" }}>
                    Sort Order
                  </th>
                  <th style={{ padding: "6px", border: "1px solid #bbb" }}>
                    Enabled
                  </th>
                </tr>
              </thead>
              <tbody>
                {menuArr.map((row, ridx) => (
                  <tr key={ridx}>
                    <td
                      style={{
                        border: "1px solid #bbb",
                        padding: "6px",
                        textAlign: "left",
                      }}
                    >
                      {row.type}
                    </td>
                    <td
                      style={{
                        border: "1px solid #bbb",
                        padding: "6px",
                        textAlign: "left",
                      }}
                    >
                      {row.pageName}
                    </td>
                    <td
                      style={{
                        border: "1px solid #bbb",
                        padding: "6px",
                        textAlign: "left",
                      }}
                    >
                      {row.displayName}
                    </td>
                    <td
                      style={{
                        border: "1px solid #bbb",
                        padding: "6px",
                        textAlign: "left",
                      }}
                    >
                      {row.type === "Static Site" ? row.TemplateId : row.url}
                    </td>
                    <td
                      style={{
                        border: "1px solid #bbb",
                        padding: "6px",
                        textAlign: "left",
                      }}
                    >
                      {row.sortOrder}
                    </td>
                    <td
                      style={{
                        border: "1px solid #bbb",
                        padding: "6px",
                        textAlign: "left",
                      }}
                    >
                      {String(row.enabled)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          );
        } catch (e) {
          rendered.push(<pre key={"err-" + i}>{arrays[i]}</pre>);
        }
      }
      if (textSegments.length > arrays.length) {
        rendered.push(
          <div key="text-last" style={{ margin: "6px 0" }}>
            {textSegments[textSegments.length - 1]}
          </div>
        );
      }
      return (
        <div
          style={{
            fontWeight: "bold",
            fontSize: "1.2rem",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            textAlign: "center",
          }}
        >
          {rendered}
        </div>
      );
    }
    return (
      <div
        style={{
          fontWeight: "bold",
          fontSize: "1.2rem",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          textAlign: "center",
        }}
      >
        {details}
      </div>
    );
  };

  return (
    <Modal
      className="audit-history-details-popup-modal"
      isOpen={isModalOpen}
      backdrop
      keyboard={false}
      wrapClassName="modal-primary"
      size="lg"
    >
      <div className="modal-title-color audit-history-editTemplateDetails-modal-title">
        <h5>
          <strong>Details</strong>
        </h5>
      </div>
      <ModalBody>
        <div className="audit-history-editTemplate-adminConfirmationModal-modal-body-content">
          {specialTypes.includes(type) ? (
            renderDetails(message)
          ) : (
            <div style={{ whiteSpace: "pre-wrap", textAlign: "left" }}>
              <strong>{message}</strong>
            </div>
          )}

          <div className="audit-history-editTemplate-adminConfirmationModal-popup-button">
            <button
              type="button"
              onClick={closeModal}
              className="btn btn-primary audit-history-editTemplate-adminConfirmationModal-button btn-rounded"
            >
              Close
            </button>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default SpecialMessageModal;



// WEBPACK FOOTER //
// ./src/mi-app/adminPanel/V2Audit/SpecialMessageModal.js